import { GitLabApiClient } from '../api';
import { log } from '../log';
import { ConfigService, IConfig } from '../config_service';
import { FixedTimeCircuitBreaker } from '../circuit_breaker/fixed_time_circuit_breaker';
import { SuggestionOption } from '../api_types';
import { InvalidInstanceVersionError } from '../fetch_error';
import {
  TRACKING_EVENTS,
  GC_TIME,
  INSTANCE_TRACKING_EVENTS_MAP,
  TELEMETRY_DISABLED_WARNING_MSG,
  TELEMETRY_ENABLED_MSG,
} from './constants';
import {
  TelemetryTracker,
  ICodeSuggestionContextUpdate,
  ITelemetryOptions,
} from './tracking_types';
import { canClientTrackEvent } from './utils';
import { CodeSuggestionTelemetryState } from './code_suggestions_telemetry_state_manager';

interface СodeSuggestionContext {
  language?: string;
  suggestion_size?: number;
  timestamp: string;
  is_streaming?: boolean;
}

export class InstanceTracker implements TelemetryTracker {
  #api: GitLabApiClient;

  #codeSuggestionsContextMap = new Map<string, СodeSuggestionContext>();

  #circuitBreaker = new FixedTimeCircuitBreaker('Instance telemetry');

  #configService: ConfigService;

  #options: ITelemetryOptions = {
    enabled: true,
    actions: [],
  };

  #suggestionStateManager: CodeSuggestionTelemetryState = new CodeSuggestionTelemetryState(
    'Instance Telemetry',
  );

  // API used for tracking events is available since GitLab v17.2.0.
  // Given the track request is done for each CS request
  // we need to  make sure we do not log the unsupported instance message many times
  #invalidInstanceMsgLogged = false;

  constructor(api: GitLabApiClient, configService: ConfigService) {
    this.#configService = configService;
    this.#configService.onConfigChange((config) => this.#reconfigure(config));
    this.#api = api;
  }

  #reconfigure(config: IConfig) {
    const { baseUrl } = config.client;
    const enabled = config.client.telemetry?.enabled;
    const actions = config.client.telemetry?.actions;

    if (typeof enabled !== 'undefined') {
      this.#options.enabled = enabled;

      if (enabled === false) {
        log.warn(`Instance Telemetry: ${TELEMETRY_DISABLED_WARNING_MSG}`);
      } else if (enabled === true) {
        log.info(`Instance Telemetry: ${TELEMETRY_ENABLED_MSG}`);
      }
    }

    if (baseUrl) {
      this.#options.baseUrl = baseUrl;
    }

    if (actions) {
      this.#options.actions = actions;
    }
  }

  isEnabled(): boolean {
    return Boolean(this.#options.enabled);
  }

  setCodeSuggestionsContext(
    uniqueTrackingId: string,
    context: Partial<ICodeSuggestionContextUpdate>,
  ) {
    if (this.#circuitBreaker.isOpen()) {
      return;
    }

    const { model, isStreaming } = context;

    // Only auto-reject if client is set up to track accepted and not rejected events.
    if (
      canClientTrackEvent(this.#options.actions, TRACKING_EVENTS.ACCEPTED) &&
      !canClientTrackEvent(this.#options.actions, TRACKING_EVENTS.REJECTED)
    ) {
      this.rejectOpenedSuggestions();
    }

    setTimeout(() => {
      if (this.#codeSuggestionsContextMap.has(uniqueTrackingId)) {
        this.#codeSuggestionsContextMap.delete(uniqueTrackingId);
        this.#suggestionStateManager.deleteSuggestion(uniqueTrackingId);
      }
    }, GC_TIME);

    this.#codeSuggestionsContextMap.set(uniqueTrackingId, {
      is_streaming: isStreaming,
      language: model?.lang,
      timestamp: new Date().toISOString(),
    });

    if (this.#isStreamingSuggestion(uniqueTrackingId)) return;

    this.updateSuggestionState(uniqueTrackingId, TRACKING_EVENTS.REQUESTED);
  }

  async updateCodeSuggestionsContext(
    uniqueTrackingId: string,
    contextUpdate: Partial<ICodeSuggestionContextUpdate>,
  ) {
    if (this.#circuitBreaker.isOpen()) {
      return;
    }

    if (this.#isStreamingSuggestion(uniqueTrackingId)) return;

    const context = this.#codeSuggestionsContextMap.get(uniqueTrackingId);
    const { model, suggestionOptions, isStreaming } = contextUpdate;

    if (context) {
      if (model) {
        context.language = model?.lang;
      }

      if (suggestionOptions?.length) {
        context.suggestion_size = InstanceTracker.#suggestionSize(suggestionOptions);
      }

      if (typeof isStreaming === 'boolean') {
        context.is_streaming = isStreaming;
      }

      this.#codeSuggestionsContextMap.set(uniqueTrackingId, context);
    }
  }

  updateSuggestionState(uniqueTrackingId: string, newState: TRACKING_EVENTS): void {
    const isStreaming = this.#isStreamingSuggestion(uniqueTrackingId);

    if (isStreaming) return;

    if (this.#suggestionStateManager.canUpdateState(uniqueTrackingId, newState, isStreaming)) {
      this.#suggestionStateManager.updateSuggestionState(uniqueTrackingId, newState, isStreaming);
      this.#trackCodeSuggestionsEvent(newState, uniqueTrackingId).catch((e) =>
        log.warn('Instance Telemetry: Could not track telemetry', e),
      );
    }
  }

  async #trackCodeSuggestionsEvent(eventType: TRACKING_EVENTS, uniqueTrackingId: string) {
    const event = INSTANCE_TRACKING_EVENTS_MAP[eventType];

    if (!event) {
      return;
    }

    try {
      const { language, suggestion_size } =
        this.#codeSuggestionsContextMap.get(uniqueTrackingId) ?? {};

      await this.#api.fetchFromApi({
        type: 'rest',
        method: 'POST',
        path: '/usage_data/track_event',
        body: {
          event,
          additional_properties: {
            unique_tracking_id: uniqueTrackingId,
            timestamp: new Date().toISOString(),
            language,
            suggestion_size,
          },
        },
        supportedSinceInstanceVersion: {
          resourceName: 'track instance telemetry',
          version: '17.2.0',
        },
      });

      this.#circuitBreaker.success();
    } catch (error) {
      if (error instanceof InvalidInstanceVersionError) {
        if (this.#invalidInstanceMsgLogged) return;

        this.#invalidInstanceMsgLogged = true;
      }

      log.warn(`Instance telemetry: Failed to track event: ${eventType}`, error);
      this.#circuitBreaker.error();
    }
  }

  rejectOpenedSuggestions() {
    log.debug(`Instance Telemetry: Reject all opened suggestions`);
    this.#suggestionStateManager
      .getOpenedSuggestions()
      .forEach((id) => this.updateSuggestionState(id, TRACKING_EVENTS.REJECTED));
  }

  static #suggestionSize(options: SuggestionOption[]): number {
    const countLines = (text: string) => (text ? text.split('\n').length : 0);

    return Math.max(...options.map(({ text }) => countLines(text)));
  }

  #isStreamingSuggestion(uniqueTrackingId: string): boolean {
    return Boolean(this.#codeSuggestionsContextMap.get(uniqueTrackingId)?.is_streaming);
  }
}
